/**
 * Returns a `resolveRemoteAssetUrl({ assetId })` callback (matching the
 * signature {@link import("/runtime/index.js").LensCoreEngineOptions.resolveRemoteAssetUrl}
 * accepts) that maps an `assetId` to a URL on a static `/precache-assets/<id>`
 * endpoint.
 *
 * The runtime's default resolver loads precache assets from a 5+ GB bundle
 * bind-mounted next to the runtime package (`runtime/precache-assets/<id>`),
 * which is fine for the host shell but inappropriate for deployed renderers.
 * This helper points at a remote origin that serves the same bundle.
 *
 * Pair with `cloudflareAccessFetchHeaders` to attach CF Access credentials
 * automatically — both helpers key off origin prefix, so the same origin
 * string can drive both.
 *
 * Passing an empty/missing `origin` returns `undefined`, which leaves the
 * engine on its default local-bundle resolver — handy as a fall-through
 * default when an env var isn't set.
 *
 * @example
 * import {
 *   cloudflareAccessFetchHeaders,
 *   precacheAssetUrlResolver,
 * } from "@lenscore-pipeline/lens-canvas";
 *
 * const PRECACHE_ORIGIN = "https://search-lens.byteo.app";
 *
 * const engineOptions = {
 *   resolveRemoteAssetUrl: precacheAssetUrlResolver(PRECACHE_ORIGIN),
 *   getLensFetchHeaders: cloudflareAccessFetchHeaders({
 *     clientId: import.meta.env.CF_ACCESS_CLIENT_ID,
 *     clientSecret: import.meta.env.CF_ACCESS_CLIENT_SECRET,
 *     originPrefixes: [PRECACHE_ORIGIN + "/"],
 *   }),
 * };
 *
 * @param {string | undefined} origin Origin like `https://search-lens.byteo.app`.
 *   Trailing slashes are stripped. Empty/missing → `undefined`.
 * @returns {((request: { assetId: string }) => string) | undefined}
 */
export function precacheAssetUrlResolver(origin) {
    const base = String(origin ?? "").trim().replace(/\/+$/, "");
    if (!base)
        return undefined;
    return ({ assetId }) => `${base}/precache-assets/${encodeURIComponent(assetId)}`;
}
//# sourceMappingURL=precacheAssets.js.map